"use client";

import Image from "next/image";
import React, { forwardRef, useEffect } from "react";
import { preload } from "react-dom";

import { useIntersectedOnce } from "@/hooks/use-intersection-once";

export interface VideoType {
  playbackId?: string;
  resolution?: string;
}

export type AnimationVideoProps = {
  loading?: "eager" | "lazy";
  posterOnly?: boolean;
  video?: VideoType;
  withSound?: boolean;
  autoPlay?: boolean;
  thumbnailTime?: number | null;
  className?: string;
} & Omit<VideoProps, "mp4Url" | "webmUrl">;

export const AnimationVideo = forwardRef<HTMLVideoElement, AnimationVideoProps>(
  (
    {
      loading,
      posterOnly,
      video,
      loop,
      autoPlay,
      thumbnailTime,
      ...videoProps
    }: AnimationVideoProps,
    ref
  ) => {
    const { playbackId, resolution } = video ?? {};

    if (!playbackId || !resolution) {
      return null;
    }

    const mp4Url = `https://stream.mux.com/${playbackId}/${
      resolution === "SD" ? "medium" : "high"
    }.mp4`;

    const webmUrl = `https://stream.mux.com/${playbackId}/${
      resolution === "SD" ? "medium" : "high"
    }.webm`;

    const poster = `https://image.mux.com/${playbackId}/thumbnail.webp?fit_mode=smartcrop&time=${thumbnailTime ?? 0}`;

    if (loading === "eager") {
      preload(poster, {
        as: "image",
        fetchPriority: "high",
      });
    }

    return (
      <Video
        loading={loading}
        mp4Url={mp4Url}
        poster={poster}
        posterOnly={posterOnly}
        src={mp4Url}
        webmUrl={webmUrl}
        ref={ref}
        loop={loop}
        autoPlay={autoPlay}
        {...videoProps}
      />
    );
  }
);

AnimationVideo.displayName = "AnimationVideo";

type VideoProps = {
  loading?: "eager" | "lazy";
  mp4Url: string;
  posterOnly?: boolean;
  webmUrl: string;
  withSound?: boolean;
  autoPlay?: boolean;
  className?: string;
} & React.DetailedHTMLProps<
  React.VideoHTMLAttributes<HTMLVideoElement>,
  HTMLVideoElement
>;

const Video = forwardRef<HTMLVideoElement, VideoProps>(
  (
    {
      loading,
      mp4Url,
      posterOnly = false,
      webmUrl,
      withSound,
      loop,
      autoPlay,
      ...props
    }: VideoProps,
    ref
  ) => {
    const [appeared, setAppeared] = React.useState(false);

    const imageRef = React.useRef<HTMLElement>(null);

    const inView = useIntersectedOnce(imageRef, {
      root: null,
      threshold: 0.7,
    });

    const hidden = loading === "lazy" && !appeared;

    useEffect(() => {
      if (inView) {
        setAppeared(true);
      }
    }, [inView]);

    return (
      <>
        {hidden || posterOnly ? (
          <Image
            alt="Video poster"
            className={props.className}
            height={345}
            loading="lazy"
            ref={imageRef as React.RefObject<HTMLImageElement>}
            src={props.poster ?? ""}
            style={props.style}
            width={444}
          />
        ) : (
          <video
            autoPlay={withSound ? false : (autoPlay ?? true)}
            controls={false}
            loop={loop}
            muted={!withSound}
            playsInline
            ref={ref || (imageRef as React.RefObject<HTMLVideoElement>)}
            {...props}
          >
            <source src={mp4Url} type="video/mp4" />
            <source src={webmUrl} type="video/webm" />
          </video>
        )}
      </>
    );
  }
);

// Optional: Set displayName for debugging purposes
Video.displayName = "Video";
